import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function DirekteBiler() {
  return (
    <>
      <SEO
        title="Express forsendelse i direkte lastbiler | Fastlane ApS"
        description="Fastlane kan via vort netværk af transportører tilbyde at sende pakker og mindre forsendelser i det meste af verdenen. Skal man sende eller modtage gods til eller fra 3. land altså lande uden for EU så skal man være opmærksom på, at der typisk skal laves en udførsel i afsender landet og en fortoldning i modtager landet."
        canonicalUrl="https://fastlane.dk/ekspres-kurer-i-direkte-lastbiler"
      />

      <PageHeader
        title="Express forsendelse i direkte lastbiler"
        subtitle="Express, Kurér og Pakker – hurtig, fleksibel dør-til-dør transport og dedikerede løsninger i hele verden."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Express forsendelse i direkte lastbiler' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Main intro from https://fastlane.dk/eksprespakke */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">Express, Kurér og Pakker</h2>
            <p>
              Fastlane kan via vort netværk af transportører tilbyde at sende pakker og mindre forsendelser i det meste af verdenen. Skal man sende eller modtage gods til eller fra 3. land altså lande uden for EU så skal man være opmærksom på, at der typisk skal laves en udførsel i afsender landet og en fortoldning i modtager landet.
            </p>
            <p className="font-semibold text-slate-900">
              Bemærk venligst Norge, UK og Schweiz er ikke med i toldunionen EU.
            </p>
            <p>
              Når der sendes pakker i et kurér system er udførsel og fortoldning typisk med, men vi skal bruge en præcis faktura og evt. en pakkeliste.
            </p>
            <p>
              Se evt. vores vejledende instruktion -{' '}
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0f2b48] font-semibold underline hover:text-[#b25d19] transition-colors"
              >
                Hvordan får jeg den rigtige fragtpris?
              </a>
            </p>
            <p>
              Vi tilbyder også at arrangere Direkte Biler ekspres service fra A til B (dør til dør).
            </p>
          </div>

          {/* Landeliste */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              I Europa sender og henter vi pakkegods til og fra:
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 text-sm text-slate-700">
              {[
                'Tyskland',
                'Benelux, d.v.s. Holland (Nederlandene), Belgien og Luxembourg',
                'Frankrig',
                'Italien',
                'Malta',
                'Cypern',
                'Spanien / Portugal',
                'Danmark',
                'Finland',
                'Sverige',
                'Grækenland',
                'UK (England) / Irland',
                'Baltikum d.s.v. Estland, Letland og Litauen',
                'Bulgarien',
                'Kroatien',
                'Polen',
                'Rumænien',
                'Slovenien',
                'Slovakiet',
                'Tjekkiet',
                'Ungarn',
                'Østrig'
              ].map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-sky-600 text-xs mt-1 shrink-0" aria-hidden="true" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Fordelene ved kurérløsninger */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">Fordelene ved kurérløsninger.</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-700">
              <li>det går hurtigt - ekspresforsendele</li>
              <li>det billigt for små pakker</li>
              <li>det er fra afsenders dør og helt frem til modtagers dør</li>
              <li>fortoldning og udførsel er en del af servicen - relevant ved 3. land, d.v.s. lande uden for EU f.eks. Schweiz, UK og Norge.</li>
              <li>normalt er der fuld track &amp; trace på entydigt sendingsnummer</li>
              <li>kurér er i nogle tilfælde konkurrencedygtigt til sammenligning med luftfragt</li>
            </ul>
          </div>

          {/* Typiske Kurérservices */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">Typiske Kurérservices</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <i className="fa-solid fa-bolt text-[#b25d19] text-xl" aria-hidden="true" />
                <h4 className="font-bold text-slate-900">Dag til dag</h4>
                <p className="text-sm text-slate-600">Express-forsendelse af dokumenter og pakker - dag til dag</p>
              </div>
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <i className="fa-solid fa-clock text-sky-700 text-xl" aria-hidden="true" />
                <h4 className="font-bold text-slate-900">Før kl. 9.00</h4>
                <p className="text-sm text-slate-600">Express-forsendelse - levering før kl. 9.00</p>
              </div>
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <i className="fa-solid fa-clock-rotate-left text-sky-700 text-xl" aria-hidden="true" />
                <h4 className="font-bold text-slate-900">Før kl. 12.00</h4>
                <p className="text-sm text-slate-600">Express-forsendelser - levering før kl. 12.00</p>
              </div>
            </div>
          </div>

          {/* Hvorfor skal vi sende dine pakker */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base border-t border-slate-200 pt-8">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              Hvorfor skal vi sende dine pakker / dokumenter med kurér? Altså du kan jo bare selv bestille direkte ved en af de store operatører?
            </h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-700">
              <li>Vi vurderer alle sendinger i forhold til hvilken transporttype (skib, fly, vej el. kurér) der er den rigtige.</li>
              <li>Er en kurérløsning optimal vurderer vi igen, hvilket kurérfirma, der er optimalt.</li>
              <li>Vi booker sendingen på den rigtige måde via det valgte kurérfirmas booking system.</li>
              <li>Vi følger op på sendingen via track and trace og slipper den først når den er leveret.</li>
              <li>Med andre ord kan du trygt outsource dine kurérforsendelser via os.</li>
              <li>Typisk vil du kunne spare penge fordi vi har bedre fragtrater end du selv kan opnå.</li>
              <li>Vi har generelt gode fragtpriser .</li>
            </ul>

            <p className="text-sm text-slate-600 pt-3 leading-relaxed">
              Præcis beskrivelse af hvert enkelt kolli er vigtig p.g.a pladsbegrænsning afhængig af kurérfirma og geografiske områder. Som minimum skal vi kende godstype, dimensioner L x B x H, vægt m.m. Se evt. vores vejledende instruktion -{' '}
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0f2b48] font-semibold underline hover:text-[#b25d19] transition-colors"
              >
                Hvordan får jeg den rigtige fragtpris?
              </a>
            </p>
          </div>

          {/* CTA Box */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              Få et uforpligtende fragtpris på international express transport med Fastlane
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              Ønsker du en god fragtpris på indenrigs- eller international express forsendelse af dit gods, så læs evt. vore instruktion til at få den rigtige fragtpris:
            </p>
            <div className="pt-1">
              <a
                href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-16-rev-00-hvordan-jeg-f-r-den-rigtige-fragtpris.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-semibold text-sky-800 bg-white hover:bg-sky-50 border border-sky-200 px-3.5 py-2 rounded-lg transition-colors gap-2"
              >
                <i className="fa-solid fa-file-pdf text-red-600 text-sm" aria-hidden="true" />
                <span>Hvordan får jeg den rigtige fragtpris? (PDF)</span>
              </a>
            </div>

            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Link
                to="/fragtrater"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 rounded-lg bg-[#b25d19] text-white text-sm font-semibold hover:bg-[#974d12] text-center"
              >
                Indsend forespørgsel
              </Link>
              <a
                href="tel:+4576121230"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-900 text-sm font-semibold hover:bg-slate-100 whitespace-nowrap text-center"
              >
                Ring +45 76 12 12 30
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
